import { useState } from "react";
import { Alert, Button, CardActions } from "@mui/material";
import CancelIcon from "@mui/icons-material/Cancel";
import CheckIcon from "@mui/icons-material/Check";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import DownloadIcon from "@mui/icons-material/Download";
import { useAuthContext } from "features/auth/AuthContext";
import { UserRole } from "features/auth/types/user";
import { useTimeEntriesContext } from "../TimeEntriesContext";
import SimpleDialog from "components/SimpleDialog";
import { useBatchUpdateProcessedMutation, useBatchUpdateStateMutation } from "app/api/absence";
import { downloadCsv } from "../downloadCsv";
import { getErrorMessage } from "utilities/getErrorMessage";

const TimeEntryActions: React.FC<{ filters: { fromDate: Date; toDate: Date } }> = ({ filters }) => {
  const { user } = useAuthContext();
  const { entries, selectedEntries } = useTimeEntriesContext();

  const [showApproveDialog, setShowApproveDialog] = useState(false);
  const [showDenyDialog, setShowDenyDialog] = useState(false);
  const [showProcessDialog, setShowProcessDialog] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [setBatchState, { isLoading: isBatchStateLoading }] = useBatchUpdateStateMutation();
  const [setBatchProcessed, { isLoading: isBatchProcessedLoading }] =
    useBatchUpdateProcessedMutation();

  function closeDialogs() {
    setShowApproveDialog(false);
    setShowDenyDialog(false);
    setShowProcessDialog(false);
    setError(null);
  }

  return (
    <CardActions sx={{ p: 0, pt: 1, justifyContent: "flex-end" }}>
      {(user?.role === UserRole.PAYROLL || user?.role === UserRole.ADMIN) && (
        <Button
          variant="outlined"
          disabled={entries.length === 0}
          onClick={() => downloadCsv(entries, filters.fromDate, filters.toDate)}
          startIcon={<DownloadIcon />}
        >
          Exportera
        </Button>
      )}

      {user?.role === UserRole.PAYROLL && (
        <Button
          variant="contained"
          disabled={selectedEntries.length === 0}
          startIcon={<CheckIcon />}
          onClick={() => setShowProcessDialog(true)}
        >
          Behandla
        </Button>
      )}

      {user?.role === UserRole.ADMIN && (
        <>
          <Button
            variant="contained"
            color="error"
            disabled={selectedEntries.length === 0}
            startIcon={<CancelIcon />}
            onClick={() => setShowDenyDialog(true)}
          >
            Neka
          </Button>

          <Button
            variant="contained"
            color="success"
            disabled={selectedEntries.length === 0}
            startIcon={<CheckCircleIcon />}
            onClick={() => setShowApproveDialog(true)}
          >
            Godkänn
          </Button>
        </>
      )}

      {showApproveDialog && (
        <SimpleDialog
          title="Godkänn markerade?"
          confirmProps={{
            color: "success",
            children: "Godkänn",
            startIcon: <CheckCircleIcon />,
          }}
          loading={isBatchStateLoading}
          onClose={closeDialogs}
          onConfirm={() =>
            setBatchState({
              absenceIds: selectedEntries,
              state: "APPROVED",
            })
              .unwrap()
              .then(closeDialogs)
              .catch((e) => setError(getErrorMessage(e)))
          }
        >
          {error && <Alert severity="error">{error}</Alert>}
        </SimpleDialog>
      )}

      {showDenyDialog && (
        <SimpleDialog
          title="Neka markerade?"
          confirmProps={{
            color: "error",
            children: "Neka",
            startIcon: <CancelIcon />,
          }}
          loading={isBatchStateLoading}
          onClose={closeDialogs}
          onConfirm={() =>
            setBatchState({
              absenceIds: selectedEntries,
              state: "REJECTED",
            })
              .unwrap()
              .then(closeDialogs)
              .catch((e) => setError(getErrorMessage(e)))
          }
        >
          {error && <Alert severity="error">{error}</Alert>}
        </SimpleDialog>
      )}

      {showProcessDialog && (
        <SimpleDialog
          title="Behandla markerade?"
          confirmProps={{
            children: "Behandla",
            startIcon: <CheckIcon />,
          }}
          loading={isBatchProcessedLoading}
          onClose={closeDialogs}
          onConfirm={() =>
            setBatchProcessed({
              absenceIds: selectedEntries,
              processed: true,
            })
              .unwrap()
              .then(closeDialogs)
              .catch((e) => setError(getErrorMessage(e)))
          }
        >
          {error && <Alert severity="error">{error}</Alert>}
        </SimpleDialog>
      )}
    </CardActions>
  );
};

export default TimeEntryActions;
